import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import {useState} from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from "@mui/material/Grid";
import Stack from '@mui/material/Stack';

// import * as validations from '../../helpers/validations';
import {getCaseBillingTypes, getCaseStatuses} from '../../helpers/selectOptions';
import useUser from '../../hooks/useUser';
import {useGetICD10CodesQuery} from '../../redux/services/codeApi';
import {useGetDiagnosisCategoriesQuery} from '../../redux/services/diagnosisCategoryApi';
import {useGetCasesQuery, useCreateCaseMutation, useUpdateCaseMutation} from '../../redux/services/caseApi';
import {useGetPatientsQuery,useUpdatePatientMutation}  from '../../redux/services/patientApi';
import FieldInput from '../common/FormInput';
import LoadingScreen from '../common/LoadingScreen';
import CaseSessionLinkingButton from './CaseSessionLinkingButton';
import PatientSexAlert from './PatientSexAlert';

const getFields = ({
  diagnosisCodes, 
  diagnosisCategories, 
  formValues,
}) => ([
  {
    label: 'Patient',
    name: 'patient_id',
    type: 'selectPatient',
    editableAfterCreate: false,
    required: true,
    fullWidth: true,
    allPatients: false,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    label: 'Primary Dx',
    name: 'diagnosis_codes',
    type: 'select',
    editableAfterCreate: true,
    helperText: null,
    required: true,
    fullWidth: true,
    validate: () => true,
    options: (
      (diagnosisCodes && diagnosisCodes.length ? diagnosisCodes : [])
      .map(c => ({
        value: c.id,
        label: `${c.code} ${c.description}`,
      }))
    ),
    multiple: true,
    filterSelectedOptions: true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    label: 'Billing Type',
    name: 'billing_category',
    type: 'select',
    editableAfterCreate: false,
    helperText: null,
    placeholder: null,
    required: true,
    fullWidth: true,
    validate: () => true,
    options: getCaseBillingTypes(),
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Status',
    name: 'status',
    type: 'select',
    editableAfterCreate: true,
    helperText: null,
    placeholder: null,
    required: true,
    fullWidth: true,
    validate: () => true,
    options: getCaseStatuses(),
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Name',
    name: 'diagnosis_categories',
    type: 'select',
    editableAfterCreate: true,
    helperText: null,
    required: true,
    fullWidth: true,
    multiple: true,
    filterSelectedOptions: true,
    options: (
      (diagnosisCategories && diagnosisCategories.length ? diagnosisCategories : [])
      .map(c => ({
        value: c.id,
        label: `${c.name}`,
      }))
    ),
    getValue: () => formValues?.diagnosis_categories,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    }
  },
  {
    label: 'Payer Plan',
    name: 'payer_plan_id',
    type: 'selectPayerPlan',
    editableAfterCreate: true,
    placeholder: 'Select a plan',
    helperText: null,
    required: false,
    fullWidth: true,
    allowCreate: true,
    allowEdit: true,
    billingTypes: ['DIRECT_BILL', 'COURTESY_BILL'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Assignment of Benefits',
    name: 'should_assign_benefits',
    type: 'boolean',
    helperText: null,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['COURTESY_BILL'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
      paddingTop: '3.5em',
    }
  },
]);

const parseFormValues = formValues => {
  let data = omit(formValues, 'date_of_injury', 'diagnosis_codes', 'diagnosis_categories', 'copay');
  data.diagnosis_codes = (formValues.diagnosis_codes || []).map(c => c.value || c);
  data.diagnosis_categories = (formValues.diagnosis_categories || []).map(c => c.value || c);
  if (formValues?.copay) data.copay = parseFloat(formValues.copay);
  if (formValues?.billingCategory !== 'COURTESY_BILL') {
    formValues.should_assign_benefits = true; // must be true
  }
  return data;
};

const handleFieldUpdate = ({formValues, setFormValues, diagnosisCodes}) => fieldName => e => {
  let value;
  let values;
  if (fieldName === 'patient_id') {
    value = e?.patientId;
  } else if (fieldName === 'should_assign_benefits') {
    value = e?.target?.checked;
    console.log('BENVAL', value);
  } else {
    value = e?.target?.value || '';
  }
  values = {
    ...formValues,
    [fieldName]: value,
  };
  if (
    fieldName === 'diagnosis_codes' 
    && !formValues.diagnosis_categories
  ) {
    const option = values?.diagnosis_codes?.[0];
    const code = option?.value ? (diagnosisCodes || []).find(c => c.id === option.value) : null;
    const category = code?.diagnosis_category;
    if (category) values.diagnosis_categories = [{value: category.id, label: category.name}];
  }
  if (fieldName === 'billing_category' && value !== 'COURTESY_BILL') {
    values.should_assign_benefits = true; // must be true
  }
  return setFormValues(values);
};

const handleSubmit = ({
  editing, 
  providerId,
  id,
  post,
  put,
  afterSubmit
}) => async (e, formValues) => {
  let request;
  if (editing) {
    request = await put({id, body: omit(formValues, 'patient_id')});
  } else {
    request = await post({provider_id: providerId, ...formValues});
  }
  if (!request?.error && afterSubmit) {
    afterSubmit(e, {id: id || request?.data?.id});
  }
};

const CaseForm = props => {
  const {
    caseData: c, 
    diagnosisCategories,
    diagnosisCodes, 
    editing,
    afterSubmit,
    session,
  } = props;
  const [formValues, setFormValues] = useState(
    props.editing 
    ? pick(
        c,
        ...(
          props?.editableFields
          || ['billing_category', 'status', 'patient_id', 'payer_plan_id', 'diagnosis_codes', 'name']
        )
    )
    : {status: 'ACTIVE', billing_category: 'CASH', patient_id: props?.patientId},
  );
  const [put, putResponse] = useUpdateCaseMutation();
  const [post, postResponse] = useCreateCaseMutation();
  const response = editing ? putResponse : postResponse;
  const patientQuery = useGetPatientsQuery(
    {id: formValues?.patient_id}, 
    {skip: !formValues?.patient_id}
  );
  const patient = patientQuery?.data?.data?.[0] || session?.patient;
  const onSubmit = handleSubmit({editing, id: c.id, put, post, afterSubmit});
  const fields = getFields({diagnosisCodes, diagnosisCategories, formValues})
    // filter out billing fields that are not needed
    .filter(f => props?.editableFields ? props.editableFields.includes(f.name) : true)
    .filter(f => !f.billingTypes || f.billingTypes.includes(formValues.billing_category))
    // hide name field if diagnosis_codes are not present
    .filter(f => 
      f.name === 'diagnosis_categories' 
      ? isArray(formValues?.diagnosis_codes)
      : true
    ).map(f =>
      f.name === 'diagnosis_categories'
      ? {...f, value: formValues?.diagnosis_categories}
      : f
    );
  const patientSexNeeded = props?.requirePatientSex && !patient?.sex;
  const disabled = props?.disabled || patientSexNeeded;
  return (
    <Box sx={{width: '100%'}}>
      <Stack spacing={1}>
        {patientSexNeeded && patient &&
          <PatientSexAlert patient={patient || session?.patient} />
        }
        {
          <Grid container spacing={2} direction={{xs: 'row'}} padding={2}>
            {fields.map(f =>
                <Grid 
                  item 
                  xs={f.grid?.xs ?? 12} 
                  sm={f.grid?.sm} 
                  md={f.grid?.md}
                  style={{paddingTop: f.grid?.paddingTop}}
                >
                  <FieldInput
                    defaultValue={c?.[f.name] || (
                      f.name === 'diagnosis_categories'
                      ? (formValues?.diagnosis_categories || []).map(c => c.value)
                      : null
                    )}
                    onChange={handleFieldUpdate({formValues, setFormValues, diagnosisCodes})(f.name)}
                    formValues={formValues}
                    patientId={formValues.patient_id || props?.patientId}
                    planCategory={formValues.billing_category}
                    {...f}
                    disabled={
                      f?.disabled 
                      || (props.editing && !f.editableAfterCreate)
                    }
                  />
                </Grid>
              )
            }
            {editing && 
              <Grid item xs={12}>
                <CaseSessionLinkingButton caseData={props.originalCase}/>
              </Grid>
            }
            {response.error && 
              <Grid item xs={12}>
                <Alert severity='error'>
                  {response.error?.data?.errors?.[0] || 'An error occurred while saving the payer plan.'}
                </Alert>
              </Grid>
            }
            <Grid item xs={12}>
              <Stack alignItems='center'>
                <Box sx={{width: 'fit-content'}}>
                  <Button
                    variant='primaryGradient'
                    disabled={disabled}
                    onClick={e => onSubmit(e, parseFormValues({...formValues, }))}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        }
      </Stack>
    </Box>
  );
};

const CaseFormContainer = props => {
  const {caseId, editing} = props;
  const caseQuery = useGetCasesQuery({id: props.caseId}, {skip: !editing || !caseId});
  let caseData = caseQuery?.data?.data?.[0];
  if (caseData && caseData.diagnosis_codes) {
    
  }
  const icd10Res = useGetICD10CodesQuery();
  const diagnosisCodes = icd10Res?.data?.data;
  const diagnosisCategoryQuery = useGetDiagnosisCategoriesQuery({});
  const diagnosisCategories = diagnosisCategoryQuery?.data?.data;
  const user = useUser();
  const providerId = user?.provider?.id;
  const caseDataFormatted = {
    ...caseData,
    diagnosis_codes: (caseData?.diagnosis_codes || []).map(c => c.id),
    diagnosis_categories: (caseData?.diagnosis_categories || []).map(c => c.id),
    // diagnosis_codes: (caseData?.diagnosis_codes || []).map(c => ({
    //   value: c.id,
    //   label: `${c.code} ${c.description}`,
    // })),
  };
  const childProps = {
    ...props,
    providerId,
    caseData: caseDataFormatted,
    originalCase: caseData,
    patientId: props?.patientId || caseData?.patient_id,
    diagnosisCodes,
    diagnosisCategories,
    disabled: props?.disabled,
  };
  if (!providerId || (editing && !caseData) || !diagnosisCodes?.length) return <LoadingScreen/>;
  return (
    <CaseForm {...childProps} />
  );
};

CaseFormContainer.defaultProps = {
  onSubmitted: () => (true),
  editing: false,
};

CaseFormContainer.propTypes = {
  case: PropTypes.object,
  onSubmitted: PropTypes.func,
  editing: PropTypes.bool,
};

export default CaseFormContainer;
